import React, { useMemo, useState, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Select, SelectItem } from "@heroui/react";
import AppButton from "../common/AppButton";

export default function SpeakersModal({ isOpen, onClose, speakers = [], onRename, onAdd, onSwap }) {
  const [editingKey, setEditingKey] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [swapFrom, setSwapFrom] = useState(new Set());
  const [swapTo, setSwapTo] = useState(new Set());

  const [newName, setNewName] = useState("");

  // Local copy to reflect immediate edits while modal is open
  const [localSpeakers, setLocalSpeakers] = useState([]);
  useEffect(() => {
    setLocalSpeakers(Array.isArray(speakers) ? speakers.slice() : []);
    if (isOpen) {
      setSwapFrom(new Set());
      setSwapTo(new Set());
    }
  }, [speakers, isOpen]);

  const sortedSpeakers = useMemo(() => {
    return [...(localSpeakers || [])].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [localSpeakers]);

  const beginEdit = (sp) => {
    setEditingKey(sp.key);
    setEditingName(sp.label || sp.key || "");
  };

  const commitEdit = () => {
    if (!editingKey) return;
    const trimmed = (editingName || "").trim();
    if (!trimmed) return;
    // Update UI immediately
    setLocalSpeakers((prev) =>
      (prev || []).map((s) => (s.key === editingKey ? { ...s, label: trimmed } : s))
    );
    onRename?.(editingKey, trimmed);
    setEditingKey(null);
    setEditingName("");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingName("");
  };

  const handleAdd = () => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    onAdd?.(trimmed);
    // Optimistic add to local list
    setLocalSpeakers((prev) => {
      const exists = (prev || []).some((s) => s.key === trimmed);
      if (exists) return prev;
      return [...(prev || []), { key: trimmed, label: trimmed }];
    });
    setNewName("");
  };

  const handleSwap = () => {
    const fromKey = Array.from(swapFrom || [])[0];
    const toKey = Array.from(swapTo || [])[0];
    if (!fromKey || !toKey || fromKey === toKey) return;
    onSwap?.(fromKey, toKey);
    setLocalSpeakers((prev) => {
      const list = prev || [];
      const from = list.find((s) => s.key === fromKey);
      const to = list.find((s) => s.key === toKey);
      if (!from || !to) return prev;
      const fromLabel = from.label || from.key;
      const toLabel = to.label || to.key;
      return list.map((s) => {
        if (s.key === fromKey) return { ...s, label: toLabel };
        if (s.key === toKey) return { ...s, label: fromLabel };
        return s;
      });
    });
    setSwapFrom(new Set());
    setSwapTo(new Set());
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose} size="lg" scrollBehavior="inside" placement="center">
      <ModalContent>
        {(close) => (
          <>
            <ModalHeader className="text-base font-semibold">Sprecher verwalten</ModalHeader>
            <ModalBody className="space-y-4">
              <div className="space-y-2">
                {sortedSpeakers.map((sp) => {
                  const isEditing = editingKey === sp.key;
                  return (
                    <div
                      key={sp.key}
                      className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        beginEdit(sp);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isEditing ? (
                        <>
                          <div className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{sp.label || sp.key}</span>
                            {sp.value != null && <span className="ms-2 text-xs text-gray-500">#{sp.value}</span>}
                          </div>
                            <AppButton
                              width="w-auto"
                              roundedFull="rounded-lg"
                              className="px-3 py-1.5 text-xs"
                              onClick={(e) => {
                                if (e?.stopPropagation) e.stopPropagation();
                                beginEdit(sp);
                              }}
                            >
                              Umbenennen
                            </AppButton>
                        </>
                      ) : (
                        <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                          <Input
                            size="sm"
                            placeholder="Neuer Name"
                            value={editingName}
                            onValueChange={setEditingName}
                            className="flex-1"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                          />
                          <AppButton
                            width="w-auto"
                            roundedFull="rounded-lg"
                            className="px-3 py-1.5 text-xs"
                            onClick={commitEdit}
                          >
                            Speichern
                          </AppButton>
                          <AppButton
                            width="w-auto"
                            roundedFull="rounded-lg"
                            className="px-3 py-1.5 text-xs"
                            onClick={cancelEdit}
                          >
                            Abbrechen
                          </AppButton>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm font-medium">Sprecher tauschen</div>
                <div className="flex flex-col gap-2">
                  <Select
                    size="sm"
                    label="Von"
                    placeholder="Sprecher auswählen"
                    selectedKeys={swapFrom}
                    onSelectionChange={setSwapFrom}
                  >
                    {sortedSpeakers.map((sp) => (
                      <SelectItem key={sp.key} value={sp.key}>
                        {sp.label || sp.key}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    size="sm"
                    label="Zu"
                    placeholder="Sprecher auswählen"
                    selectedKeys={swapTo}
                    onSelectionChange={setSwapTo}
                  >
                    {sortedSpeakers.map((sp) => (
                      <SelectItem key={sp.key} value={sp.key}>
                        {sp.label || sp.key}
                      </SelectItem>
                    ))}
                  </Select>
                  <AppButton
                    width="w-auto"
                    roundedFull="rounded-lg"
                    className="px-3 py-1.5 text-xs"
                    disabled={
                      !Array.from(swapFrom || []).length ||
                      !Array.from(swapTo || []).length ||
                      Array.from(swapFrom || [])[0] === Array.from(swapTo || [])[0]
                    }
                    onClick={handleSwap}
                  >
                    Tauschen
                  </AppButton>
                </div>
              </div>
              <div className="border-t pt-3" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm font-medium mb-2">Neuen Sprecher hinzufügen</div>
                <div className="flex items-center gap-2">
                  <Input
                    size="sm"
                    placeholder="Name"
                    value={newName}
                    onValueChange={setNewName}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAdd();
                    }}
                  />
                  <AppButton
                    width="w-auto"
                    roundedFull="rounded-lg"
                    className="px-3 py-1.5 text-xs"
                    onClick={handleAdd}
                  >
                    Hinzufügen
                  </AppButton>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <AppButton
                width="w-auto"
                roundedFull="rounded-lg"
                className="px-3 py-1.5 text-xs"
                onClick={close}
              >
                Schließen
              </AppButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
